import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'

export default function AuthCallback() {
  const navigate = useNavigate()

  useEffect(() => {
    const handleAuthCallback = async () => {
      console.log('AuthCallback: Starting callback processing...')
      
      // Check if Supabase client is available
      if (!supabase) {
        console.error('Supabase client is not available for auth callback')
        navigate('/auth/signin?error=Configuration error')
        return
      }

      try {
        // Handle the OAuth callback by exchanging the code
        console.log('AuthCallback: Exchanging auth code from URL...')
        const { data, error } = await supabase.auth.exchangeCodeForSession(window.location.search)
        
        if (error) {
          console.error('Auth callback exchange error:', error)
          // If code exchange fails, try getting existing session
          const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
          if (sessionError || !sessionData.session) {
            navigate('/auth/signin?error=Authentication failed')
            return
          }
          // Use the existing session if code exchange failed but session exists
          console.log('AuthCallback: Using existing session')
          data.session = sessionData.session
          data.user = sessionData.session.user
        }

        console.log('AuthCallback: Session data:', { hasSession: !!data.session, hasUser: !!data.session?.user })

        if (data.session?.user) {
          console.log('AuthCallback: User authenticated, checking profile...')
          
          // Check if profile exists, create if it doesn't
          const { error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', data.session.user.id)
            .single()

          if (profileError && profileError.code === 'PGRST116') {
            console.log('AuthCallback: Creating new profile...')
            // Profile doesn't exist, create it
            const { error: createError } = await supabase
              .from('profiles')
              .insert({
                id: data.session.user.id,
                email: data.session.user.email!,
                display_name: data.session.user.user_metadata?.full_name || data.session.user.user_metadata?.name || null,
                role: 'user'
              })

            if (createError) {
              console.error('Error creating profile:', createError)
            } else {
              console.log('AuthCallback: Profile created successfully')
            }
          }

          console.log('AuthCallback: Redirecting to dashboard...')
          navigate('/')
        } else {
          console.log('AuthCallback: No user session, redirecting to signin...')
          navigate('/auth/signin')
        }
      } catch (error) {
        console.error('Unexpected error during auth callback:', error)
        navigate('/auth/signin?error=Authentication failed')
      }
    }

    // Add a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      console.error('AuthCallback: Timeout after 10 seconds, redirecting to signin')
      navigate('/auth/signin?error=Authentication timeout')
    }, 10000)

    handleAuthCallback().finally(() => {
      clearTimeout(timeout)
    })

    return () => {
      clearTimeout(timeout)
    }
  }, [navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Completing sign-in...</p>
      </div>
    </div>
  )
}